// =============================================================================
// EleicaoView.ts — Tela pré-partida das eleições com seed (#101/#97): mostra o
// edital do período (GET /api/eleicao) antes de abrir as urnas — modificadores
// do dia, tarja de ÚLTIMO DIA DE VOTAÇÃO, streak "fidelidade partidária",
// avisos de tentativa usada/sem login/modo dev e a escolha de candidato
// (bloqueada quando o Voto Impresso fixa o personagem do dia).
// =============================================================================
import { CHARACTER_TABLE } from '../../../shared/characters';
import {
  ELEICAO_MODIFIER_TABLE,
  type EleicaoMode, type EleicaoPeriodoInfo, type EleicaoStatusResponse,
} from '../../../shared/eleicao';
import { audio } from '../AudioEngine';
import { $, charImg, showScreen, splitNome } from './dom';
import { CharGrid } from './CharGrid';
import { eleicaoPixelIcon } from './pixelIcons';

// Texto vindo do servidor entra em innerHTML: escapa antes que alguém se
// eleja com <script> no nome
const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

// "faltam 2d 3h" / "faltam 5h 12min" — contagem estática do edital
const fmtFechaEm = (secs: number): string => {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  return `${m}min`;
};

export class EleicaoView {
  private mode: EleicaoMode = 'diaria';
  private selectedChar = 'lula';
  private charFixo: string | null = null;
  private seq = 0;                        // descarta respostas fora de ordem
  private readonly charGrid = new CharGrid($('eleicao-char-grid'), true);
  private readonly onCharPick: (charId: string) => void;

  constructor(handlers: {
    onBack: () => void;
    onStart: (mode: EleicaoMode, charId: string) => void;
    onCharPick: (charId: string) => void;   // mantém a escolha sincronizada com o app
  }) {
    this.onCharPick = handlers.onCharPick;
    $('btn-eleicao-back').onclick = () => {
      audio.sfx.urnaKey();
      handlers.onBack();
    };
    $('btn-eleicao-start').onclick = () => {
      audio.sfx.urnaConfirma();
      // Trava o botão na hora: double-click não dispara dois eleicao:start
      // (o servidor também barra, mas aqui evita até a tentativa)
      this.setStartEnabled(false);
      handlers.onStart(this.mode, this.charFixo ?? this.selectedChar);
    };
  }

  // Habilita/trava o botão ABRIR AS URNAS (falha no start reabilita)
  setStartEnabled(enabled: boolean): void {
    ($('btn-eleicao-start') as HTMLButtonElement).disabled = !enabled;
  }

  show(mode: EleicaoMode, selectedChar: string): void {
    this.mode = mode;
    this.selectedChar = selectedChar;
    this.charFixo = null;
    showScreen('eleicao');
    this.setError('');
    this.setStartEnabled(false);
    $('eleicao-titulo-texto').textContent = mode === 'diaria' ? 'ELEIÇÃO DO DIA' : 'ELEIÇÃO DA SEMANA';
    $('eleicao-titulo-icone').innerHTML = eleicaoPixelIcon(mode, 26);
    $('eleicao-tarja').style.display = 'none';
    $('eleicao-info').innerHTML = '<p class="zona-vazia">Consultando o edital no cartório eleitoral...</p>';
    $('eleicao-mods').innerHTML = '';
    $('eleicao-avisos').innerHTML = '';
    $('eleicao-char-fixo').style.display = 'none';
    $('eleicao-char-grid').style.display = 'none';
    void this.load();
  }

  setError(msg: string): void {
    $('eleicao-error').textContent = msg;
  }

  private async load(): Promise<void> {
    const meuSeq = ++this.seq;
    let data: EleicaoStatusResponse;
    try {
      const res = await fetch('/api/eleicao');
      data = (await res.json()) as EleicaoStatusResponse;
      if (!data.ok) throw new Error('resposta não-ok');
    } catch {
      if (meuSeq === this.seq) {
        $('eleicao-info').innerHTML =
          '<p class="zona-vazia">O cartório caiu no meio do edital. Tente de novo em instantes.</p>';
      }
      return;
    }
    if (meuSeq !== this.seq) return;   // o eleitor já trocou de tela
    this.render(data);
  }

  private render(data: EleicaoStatusResponse): void {
    const info = this.mode === 'diaria' ? data.diaria : data.semanal;

    // Tarja de escassez temporal: domingo antes das 17h é o ÚLTIMO DIA
    $('eleicao-tarja').style.display = this.mode === 'semanal' && info.ultimoDia ? '' : 'none';

    this.renderInfo(data, info);
    this.renderMods(info);
    this.renderAvisos(data, info);
    this.renderChar(info);

    this.setStartEnabled(true);
  }

  private renderInfo(data: EleicaoStatusResponse, info: EleicaoPeriodoInfo): void {
    const linhas: string[] = [];
    if (this.mode === 'diaria') {
      linhas.push(`<b>Seção do dia ${esc(info.period)}</b> — a MESMA eleição pra todo mundo: `
        + 'mesmos spawns, mesma urna, mesmo cassino (seed do dia).');
      linhas.push('Partida SOLO, dificuldade Normal. <b>1 tentativa pontuável por dia</b> por conta '
        + `— o placar zera à meia-noite de Brasília (faltam ${fmtFechaEm(info.fechaEm)}).`);
    } else {
      linhas.push(`<b>Semana ${esc(info.period)}</b> — a mesma seed a semana inteira, sem modificadores: `
        + 'só você, a horda e o cronômetro.');
      linhas.push('Partida SOLO, dificuldade Normal. <b>Tentativas ilimitadas</b>: vale o MELHOR score. '
        + (info.aberta
          ? `A boca de urna fecha domingo às 17h de Brasília (faltam ${fmtFechaEm(info.fechaEm)}).`
          : 'A votação desta semana JÁ FECHOU (domingo, 17h) — apuração em andamento.'));
    }
    if (data.streak !== null && data.streak > 0) {
      linhas.push(`${eleicaoPixelIcon('fidelidade', 16)} <b>FIDELIDADE PARTIDÁRIA:</b> `
        + `${data.streak} dia${data.streak > 1 ? 's' : ''} consecutivo${data.streak > 1 ? 's' : ''} de voto. Não quebre a corrente.`);
    }
    $('eleicao-info').innerHTML = linhas.map((l) => `<p class="eleicao-linha">${l}</p>`).join('');
  }

  private renderMods(info: EleicaoPeriodoInfo): void {
    const box = $('eleicao-mods');
    box.innerHTML = '';
    if (this.mode === 'semanal') return;   // semanal roda a seed pura
    for (const id of info.modifiers) {
      const def = ELEICAO_MODIFIER_TABLE[id];
      if (!def) continue;
      const card = document.createElement('div');
      card.className = 'eleicao-mod';
      card.innerHTML = `
        ${eleicaoPixelIcon(id, 26)}
        <span class="eleicao-mod-texto">
          <b>${esc(def.nome)}</b>
          <small>${esc(def.desc)}</small>
        </span>`;
      box.appendChild(card);
    }
  }

  private renderAvisos(data: EleicaoStatusResponse, info: EleicaoPeriodoInfo): void {
    const avisos: string[] = [];
    if (!data.configured) {
      avisos.push('Servidor sem banco de dados (modo dev): dá pra jogar, mas o placar está indisponível.');
    } else if (this.mode === 'diaria') {
      if (!data.logado) {
        avisos.push('Sem login, a partida é AMISTOSA: entre com o Google pra pontuar no placar do dia.');
      } else if (data.jaPontuou) {
        avisos.push('Você JÁ usou a tentativa pontuável de hoje — esta partida será amistosa (o voto não se devolve).');
      }
    } else if (!info.aberta) {
      avisos.push('Urnas da semana FECHADAS: dá pra treinar, mas a pontuação só volta a valer na segunda-feira.');
    }
    $('eleicao-avisos').innerHTML = avisos
      .map((a) => `<p class="eleicao-aviso">${a}</p>`)
      .join('');
  }

  private renderChar(info: EleicaoPeriodoInfo): void {
    const fixoBox = $('eleicao-char-fixo');
    const gridBox = $('eleicao-char-grid');
    if (info.charId && CHARACTER_TABLE[info.charId]) {
      // Voto Impresso: o personagem do dia é o mesmo pra todo mundo
      this.charFixo = info.charId;
      const ch = CHARACTER_TABLE[info.charId]!;
      const { nome, titulo } = splitNome(ch.nome);
      fixoBox.innerHTML = `
        ${charImg(ch.id, 52, true)}
        <span class="eleicao-fixo-texto">
          <small>VOTO IMPRESSO — CANDIDATO ÚNICO DO PERÍODO</small>
          <b>${esc(nome)}</b> <span class="eleicao-fixo-titulo">${esc(titulo)}</span>
        </span>`;
      fixoBox.style.display = '';
      gridBox.style.display = 'none';
      return;
    }
    this.charFixo = null;
    fixoBox.style.display = 'none';
    gridBox.style.display = '';
    this.paintGrid();
  }

  private paintGrid(): void {
    this.charGrid.render(this.selectedChar, (id) => {
      this.selectedChar = id;
      audio.sfx.urnaKey();
      audio.selectVoice(id);
      this.onCharPick(id);
      this.paintGrid();
    });
  }
}
